import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { apiRecipientsIndex, type Recipient } from "../lib/api";

type RecipientAccount = {
  id?: number;
  currency?: string;
  iban?: string | null;
};

export default function TransferCreate() {
  const navigate = useNavigate();
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const items = await apiRecipientsIndex();
        setRecipients(items ?? []);
      } catch (e: any) {
        console.error("Failed to load recipients", e);
        setError(e?.message ?? "Failed to load recipients.");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const onPick = (recipient_id: number) => {
    const picked = recipients.find((r) => r.id === recipient_id) || null;

    // Pass both query param (for deep-link parity) and full recipient state.
    navigate(`/transfers/create/details?recipient_id=${recipient_id}`, {
      state: {
        recipientId: recipient_id,
        recipient: picked,
      },
    });
  };

  return (
    <div className="page py-6">
      <div className="max-w-6xl mx-auto section-box p-6 space-y-6">
        {/* Top bar */}
        <div className="flex justify-between items-center">
          <h3 className="title-page">Select a Recipient</h3>
          <Link to="/recipients/create" className="btn btn-white">
            Add Recipient
          </Link>
        </div>

        {/* Errors */}
        {error && (
          <div className="alert-danger text-sm">
            {error}
          </div>
        )}

        {/* Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <div className="skeleton h-24 rounded" />
            <div className="skeleton h-24 rounded" />
            <div className="skeleton h-24 rounded" />
          </div>
        ) : recipients.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {recipients.map((r) => {
              const acc: RecipientAccount | undefined = r.accounts?.[0];
              const currency =
                acc?.currency || r.currency || "?";

              return (
                <button
                  key={r.id}
                  onClick={() => onPick(r.id)}
                  className="w-full text-left p-4 bg-gray-50 dark:bg-gray-700 rounded-xl shadow-sm hover:bg-gray-100 dark:hover:bg-gray-600 transition"
                >
                  <p className="text-base font-semibold text-gray-800 dark:text-white">
                    {r.name}
                  </p>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {currency.toUpperCase()}
                  </p>
                </button>
              );
            })}
          </div>
        ) : (
          <p className="text-gray-600 dark:text-gray-300 text-sm">
            No recipients found. Please add one first.
          </p>
        )}
      </div>
    </div>
  );
}
